import { Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Skeleton } from '@mui/material';
import { useParams } from 'react-router-dom';
import { CerebroBreadcrumb, CerebroPageContainer, CerebroSecondaryButton, CerebroText } from '../../shared/components';
import { useTermSheetComparisonQuery } from '../../shared/hooks';
import { ComparisonLineItem } from '../../shared/models';
import { NumberUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';

export const ComparisonReport: React.FC = () => {
	const { guid } = useParams<'guid'>();
	const comparisonQuery = useTermSheetComparisonQuery(guid);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Term Sheets &gt; Comparison Report</CerebroBreadcrumb>

			{comparisonQuery.isLoading && (
				<>
					<Skeleton height={60} />
					<Skeleton height={60} />
					<Skeleton height={60} />
				</>
			)}

			{comparisonQuery.isSuccess && (
				<>
					<CerebroSecondaryButton href={CerebroApiEndpoints.TermSheets.ComparisonExcel(guid)}>
						Export to Excel
					</CerebroSecondaryButton>

					<TableContainer sx={{ marginTop: 1 }}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>&nbsp;</TableCell>
									{comparisonQuery.data.items?.map(item => {
										return <TableCell key={item.bankGuid + '-header'}>{item.bankName}</TableCell>;
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								{comparisonQuery.data.lineItems?.map((item, index) => {
									return (
										<ComparisonItemTableRow
											key={item.name + `-${index}` + '-row'}
											item={item}
											columnCount={comparisonQuery.data.items.length}
										/>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}
		</CerebroPageContainer>
	);
};

interface ComparisonItemTableRowProps {
	item: ComparisonLineItem;
	columnCount: number;
}

interface ComparisonItemDisplayProps {
	item: ComparisonLineItem;
}

const ComparisonItemTableRow: React.FC<ComparisonItemTableRowProps> = ({ item, columnCount }) => {
	return item.isSpacing ? (
		<TableRow>
			<TableCell colSpan={columnCount + 1}>&nbsp;</TableCell>
		</TableRow>
	) : item.heading || item.subHeading ? (
		<ComparisonItemTableHeading item={item} columnCount={columnCount} />
	) : (
		<ComparisonItemTableRowDisplay key={'display-' + item.name} item={item} />
	);
};

const ComparisonItemTableHeading: React.FC<ComparisonItemTableRowProps> = ({ item, columnCount }) => {
	return item.values && item.values.length > 0 ? (
		<ComparisonItemTableRowDisplay key={'display-' + item.name} item={item} />
	) : (
		<TableRow>
			<TableCell colSpan={columnCount + 1}>
				<CerebroText>
					<strong>{item.name}</strong>
				</CerebroText>
			</TableCell>
		</TableRow>
	);
};

const ComparisonItemTableRowDisplay: React.FC<ComparisonItemDisplayProps> = ({ item }) => {
	return (
		<TableRow key={'row-' + item.name}>
			<TableCell>{item.name}</TableCell>
			{item.values.map(itemValue => {
				return itemValue.type == 'Currency' ? (
					<TableCell>{NumberUtils.FormatCurrency(NumberUtils.ParseDecimal(itemValue.value), '')}</TableCell>
				) : itemValue.type == 'Percent' ? (
					<TableCell>{itemValue.value}%</TableCell>
				) : (
					<TableCell>{itemValue.value}</TableCell>
				);
			})}
		</TableRow>
	);
};
